import type { ScreenShareQuality, VideoQuality } from '~/lib/livekit/types'
import * as Sentry from '@sentry/nuxt'
import { isMacOSDesktop } from '~/lib/platform/runtime'

const SETTINGS_STORAGE_KEY = 'iopta:settings'
// 2: echo cancellation turned off once on macOS — see `IS_MACOS_DESKTOP`.
const SETTINGS_SCHEMA_VERSION = 2

// On macOS, `echoCancellation: true` makes WebKit capture the mic through the system's voice
// processing unit, and while that runs macOS itself ducks every other app's audio (music gets
// quieter on join, and quieter still whenever you speak). The ducking lives in WebKit's process,
// out of our reach, so echo cancellation is off by default there; RNNoise still handles noise.
const IS_MACOS_DESKTOP = isMacOSDesktop()

const VIDEO_QUALITIES: VideoQuality[] = ['360p', '480p', '720p', '1080p']
const SCREEN_SHARE_QUALITIES: ScreenShareQuality[] = ['720p', '1080p', '1440p']

export type UserPresenceStatus = 'ONLINE' | 'DO_NOT_DISTURB' | 'INVISIBLE'
const USER_PRESENCE_STATUSES: UserPresenceStatus[] = ['ONLINE', 'DO_NOT_DISTURB', 'INVISIBLE']

/** Call tile layout — mirrors LiveKit's GridLayout/FocusLayout component pair. */
export type CallViewMode = 'grid' | 'focus'
const CALL_VIEW_MODES: CallViewMode[] = ['grid', 'focus']

/**
 * Chat message list appearance. `current` is the existing bubble-based layout
 * (`MessageGroup.vue`/`MessageItem.vue`, `Bubble`/`BubbleContent`) — the only one actually wired
 * up to real chat rendering right now. `discord` is a second, Discord-style flat layout
 * (colored username + timestamp above plain-text lines, no bubbles) — the setting/preview ships
 * first; the renderer itself is a separate follow-up.
 */
export type ChatStyle = 'current' | 'discord'
const CHAT_STYLES: ChatStyle[] = ['current', 'discord']

// Bounds for `audio.micSensitivity` — the 0..1 raw-analyser-level threshold above which
// `speakingAttributePublisher` considers the local mic "speaking" (see that file). Exported so
// the settings UI can map its slider (0..100, higher = more sensitive) to/from this range
// without duplicating the bounds.
export const MIN_MIC_SENSITIVITY_THRESHOLD = 0.005
export const MAX_MIC_SENSITIVITY_THRESHOLD = 0.2
export const DEFAULT_MIC_SENSITIVITY_THRESHOLD = 0.02

// Bounds for a voice channel's mic audio bitrate (kbps) — a real, server-persisted channel
// property (`Channel.bitrate` in groups.proto, validated server-side to the same 8-128 range)
// rather than a client-local preference. Exported so the channel settings UI can clamp its
// slider/preset picker without duplicating the bounds, and so `manager.ts`'s
// `audioPresetForChannel` can fall back to a sane default for channels with no bitrate set yet.
export const MIN_CHANNEL_AUDIO_BITRATE_KBPS = 8
export const MAX_CHANNEL_AUDIO_BITRATE_KBPS = 128
export const DEFAULT_CHANNEL_AUDIO_BITRATE_KBPS = 24

export function clampChannelAudioBitrateKbps(value: number): number {
  return Math.min(MAX_CHANNEL_AUDIO_BITRATE_KBPS, Math.max(MIN_CHANNEL_AUDIO_BITRATE_KBPS, Math.round(value)))
}

// Same reasoning as the bitrate bounds above: `Channel.user_limit` (groups.proto), validated
// server-side to 1-99 (UpdateChannelRequestValidator on the Channels service) - the max number
// of participants allowed in the voice room at once.
export const MIN_CHANNEL_USER_LIMIT = 1
export const MAX_CHANNEL_USER_LIMIT = 99
export const DEFAULT_CHANNEL_USER_LIMIT = 99

export function clampChannelUserLimit(value: number): number {
  return Math.min(MAX_CHANNEL_USER_LIMIT, Math.max(MIN_CHANNEL_USER_LIMIT, Math.round(value)))
}

export interface AppSettings {
    version: typeof SETTINGS_SCHEMA_VERSION
    media: {
        selectedMicId: string
        selectedCameraId: string
        selectedSpeakerId: string
    }
    audio: {
        micVolume: number
        speakerVolume: number
        noiseSuppressionEnabled: boolean
        echoCancellationEnabled: boolean
        /** Raw analyser-level threshold (see bounds above) for the "speaking" attribute. */
        micSensitivity: number
        /** Whether the mic is muted — applies on every join/channel switch, survives restart. */
        micMuted: boolean
        /** Whether incoming audio is deafened — same standing-preference treatment as `micMuted`. */
        deafened: boolean
    }
    video: {
        videoQuality: VideoQuality
        screenShareQuality: ScreenShareQuality
        /** Whether a new screen share starts with app/system audio included — off by default. */
        screenShareAudioEnabled: boolean
    }
    presence: {
        status: UserPresenceStatus
    }
    profile: {
        /** IANA timezone name (e.g. "Europe/Moscow"). Empty string = follow the system timezone. */
        timezone: string
    }
    notifications: {
        newMessages: boolean
        calls: boolean
        groupEvents: boolean
    }
    sounds: {
        messageSound: boolean
        callConnectSound: boolean
        callDisconnectSound: boolean
        screenShareSound: boolean
        /** UI/notification sound effects volume (connect, disconnect, message, etc.) — independent of `audio.speakerVolume`. */
        volume: number
    }
    callLayout: {
        viewMode: CallViewMode
        showParticipantsWithoutVideo: boolean
    }
    chat: {
        style: ChatStyle
    }
    /** Per-participant relative volume (0-200, 100 = unchanged), keyed by LiveKit identity (stable backend user id). */
    participantVolumes: Record<string, ParticipantVolume>
}

export interface ParticipantVolume {
    mic: number
    screenShare: number
    /** "Mute for me" — local-only, doesn't affect what the participant sends or what anyone else hears. */
    muted: boolean
}

const DEFAULT_PARTICIPANT_VOLUME: ParticipantVolume = { mic: 100, screenShare: 100, muted: false }

type DeepPartial<T> = {
    [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K]
}

const DEFAULT_APP_SETTINGS: AppSettings = {
    version: SETTINGS_SCHEMA_VERSION,
    media: {
        selectedMicId: 'default',
        selectedCameraId: 'default',
        selectedSpeakerId: 'default',
    },
    audio: {
        micVolume: 0.5,
        speakerVolume: 1.0,
        noiseSuppressionEnabled: true,
        echoCancellationEnabled: !IS_MACOS_DESKTOP,
        micSensitivity: DEFAULT_MIC_SENSITIVITY_THRESHOLD,
        micMuted: false,
        deafened: false,
    },
    video: {
        videoQuality: '720p',
        screenShareQuality: '1080p',
        screenShareAudioEnabled: false,
    },
    presence: {
        status: 'ONLINE',
    },
    profile: {
        timezone: '',
    },
    notifications: {
        newMessages: true,
        calls: true,
        groupEvents: false,
    },
    sounds: {
        // Off by default — the message-arrival sound was previously unconditional and unwanted.
        messageSound: false,
        callConnectSound: true,
        callDisconnectSound: true,
        screenShareSound: true,
        volume: 0.25,
    },
    callLayout: {
        viewMode: 'grid',
        showParticipantsWithoutVideo: true,
    },
    chat: {
        style: 'current',
    },
    participantVolumes: {},
}

function canUseLocalStorage() {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function cloneDefaultSettings(): AppSettings {
    return {
        version: DEFAULT_APP_SETTINGS.version,
        media: {...DEFAULT_APP_SETTINGS.media},
        audio: {...DEFAULT_APP_SETTINGS.audio},
        video: {...DEFAULT_APP_SETTINGS.video},
        presence: {...DEFAULT_APP_SETTINGS.presence},
        profile: {...DEFAULT_APP_SETTINGS.profile},
        notifications: {...DEFAULT_APP_SETTINGS.notifications},
        sounds: {...DEFAULT_APP_SETTINGS.sounds},
        callLayout: {...DEFAULT_APP_SETTINGS.callLayout},
        chat: {...DEFAULT_APP_SETTINGS.chat},
        participantVolumes: {},
    }
}

function readBoolean(value: string | null, fallback: boolean) {
    if (value === 'true') return true
    if (value === 'false') return false
    return fallback
}

function readNumber(value: string | null, fallback: number) {
    if (value === null) return fallback
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
}

function clamp01(value: number) {
    return Math.min(1, Math.max(0, value))
}

function clampVolumePercent(value: number, fallback: number) {
    return Number.isFinite(value) ? Math.min(200, Math.max(0, value)) : fallback
}

function clampMicSensitivity(value: number, fallback: number) {
    return Number.isFinite(value)
        ? Math.min(MAX_MIC_SENSITIVITY_THRESHOLD, Math.max(MIN_MIC_SENSITIVITY_THRESHOLD, value))
        : fallback
}

function normalizeParticipantVolumes(value: unknown): Record<string, ParticipantVolume> {
    if (!value || typeof value !== 'object') return {}
    const result: Record<string, ParticipantVolume> = {}
    for (const [identity, entry] of Object.entries(value as Record<string, Partial<ParticipantVolume> | undefined>)) {
        if (!identity || !entry || typeof entry !== 'object') continue
        result[identity] = {
            mic: clampVolumePercent(Number(entry.mic), DEFAULT_PARTICIPANT_VOLUME.mic),
            screenShare: clampVolumePercent(Number(entry.screenShare), DEFAULT_PARTICIPANT_VOLUME.screenShare),
            muted: Boolean(entry.muted),
        }
    }
    return result
}

function readVideoQuality(value: unknown, fallback: VideoQuality): VideoQuality {
    return typeof value === 'string' && VIDEO_QUALITIES.includes(value as VideoQuality)
        ? value as VideoQuality
        : fallback
}

function readScreenShareQuality(value: unknown, fallback: ScreenShareQuality): ScreenShareQuality {
    return typeof value === 'string' && SCREEN_SHARE_QUALITIES.includes(value as ScreenShareQuality)
        ? value as ScreenShareQuality
        : fallback
}

function readPresenceStatus(value: unknown, fallback: UserPresenceStatus): UserPresenceStatus {
    return typeof value === 'string' && USER_PRESENCE_STATUSES.includes(value as UserPresenceStatus)
        ? value as UserPresenceStatus
        : fallback
}

function readCallViewMode(value: unknown, fallback: CallViewMode): CallViewMode {
    return typeof value === 'string' && CALL_VIEW_MODES.includes(value as CallViewMode)
        ? value as CallViewMode
        : fallback
}

function readChatStyle(value: unknown, fallback: ChatStyle): ChatStyle {
    return typeof value === 'string' && CHAT_STYLES.includes(value as ChatStyle)
        ? value as ChatStyle
        : fallback
}

function mergeSettings(base: AppSettings, patch: DeepPartial<AppSettings>): AppSettings {
    return normalizeSettings({
        version: SETTINGS_SCHEMA_VERSION,
        media: {
            ...base.media,
            ...patch.media,
        },
        audio: {
            ...base.audio,
            ...patch.audio,
        },
        video: {
            ...base.video,
            ...patch.video,
        },
        presence: {
            ...base.presence,
            ...patch.presence,
        },
        profile: {
            ...base.profile,
            ...patch.profile,
        },
        notifications: {
            ...base.notifications,
            ...patch.notifications,
        },
        sounds: {
            ...base.sounds,
            ...patch.sounds,
        },
        callLayout: {
            ...base.callLayout,
            ...patch.callLayout,
        },
        chat: {
            ...base.chat,
            ...patch.chat,
        },
        participantVolumes: {
            ...base.participantVolumes,
            ...patch.participantVolumes,
        },
    })
}

function normalizeSettings(value: AppSettings): AppSettings {
    const defaults = DEFAULT_APP_SETTINGS

    return {
        version: SETTINGS_SCHEMA_VERSION,
        media: {
            selectedMicId: value.media.selectedMicId || defaults.media.selectedMicId,
            selectedCameraId: value.media.selectedCameraId || defaults.media.selectedCameraId,
            selectedSpeakerId: value.media.selectedSpeakerId || defaults.media.selectedSpeakerId,
        },
        audio: {
            micVolume: clamp01(value.audio.micVolume),
            speakerVolume: clamp01(value.audio.speakerVolume),
            noiseSuppressionEnabled: Boolean(value.audio.noiseSuppressionEnabled),
            echoCancellationEnabled: Boolean(value.audio.echoCancellationEnabled),
            micSensitivity: clampMicSensitivity(value.audio.micSensitivity, defaults.audio.micSensitivity),
            micMuted: Boolean(value.audio.micMuted),
            deafened: Boolean(value.audio.deafened),
        },
        video: {
            videoQuality: readVideoQuality(value.video.videoQuality, defaults.video.videoQuality),
            screenShareQuality: readScreenShareQuality(value.video.screenShareQuality, defaults.video.screenShareQuality),
            screenShareAudioEnabled: Boolean(value.video.screenShareAudioEnabled),
        },
        presence: {
            status: readPresenceStatus(value.presence.status, defaults.presence.status),
        },
        profile: {
            timezone: typeof value.profile.timezone === 'string' ? value.profile.timezone : defaults.profile.timezone,
        },
        notifications: {
            newMessages: Boolean(value.notifications.newMessages),
            calls: Boolean(value.notifications.calls),
            groupEvents: Boolean(value.notifications.groupEvents),
        },
        sounds: {
            messageSound: Boolean(value.sounds.messageSound),
            callConnectSound: Boolean(value.sounds.callConnectSound),
            callDisconnectSound: Boolean(value.sounds.callDisconnectSound),
            screenShareSound: Boolean(value.sounds.screenShareSound),
            volume: clamp01(value.sounds.volume),
        },
        callLayout: {
            viewMode: readCallViewMode(value.callLayout.viewMode, defaults.callLayout.viewMode),
            showParticipantsWithoutVideo: Boolean(value.callLayout.showParticipantsWithoutVideo),
        },
        chat: {
            style: readChatStyle(value.chat.style, defaults.chat.style),
        },
        participantVolumes: normalizeParticipantVolumes(value.participantVolumes),
    }
}

function readStoredSettings(): DeepPartial<AppSettings> | null {
    if (!canUseLocalStorage()) return null

    try {
        const raw = localStorage.getItem(SETTINGS_STORAGE_KEY)
        if (!raw) return null

        const parsed = JSON.parse(raw) as DeepPartial<AppSettings>
        if (parsed && typeof parsed === 'object') return parsed
    } catch (e) {
        console.warn('[AppSettings] Failed to read settings from localStorage:', e)
        Sentry.captureException(e, { level: 'warning', extra: { action: 'read_stored_settings' } })
    }

    return null
}

function readLegacySettings(): DeepPartial<AppSettings> {
    if (!canUseLocalStorage()) return {}

    return {
        media: {
            selectedMicId: localStorage.getItem('selected_mic_id') || undefined,
            selectedCameraId: localStorage.getItem('selected_camera_id') || undefined,
            selectedSpeakerId: localStorage.getItem('selected_speaker_id') || undefined,
        },
        audio: {
            speakerVolume: readNumber(localStorage.getItem('speaker_volume'), DEFAULT_APP_SETTINGS.audio.speakerVolume),
            noiseSuppressionEnabled: readBoolean(localStorage.getItem('noise_suppression_enabled'), DEFAULT_APP_SETTINGS.audio.noiseSuppressionEnabled),
            echoCancellationEnabled: readBoolean(localStorage.getItem('echo_cancellation_enabled'), DEFAULT_APP_SETTINGS.audio.echoCancellationEnabled),
        },
        video: {
            videoQuality: readVideoQuality(localStorage.getItem('video_quality'), DEFAULT_APP_SETTINGS.video.videoQuality),
            screenShareQuality: readScreenShareQuality(localStorage.getItem('screen_share_quality'), DEFAULT_APP_SETTINGS.video.screenShareQuality),
        },
    }
}

export function loadAppSettings(): AppSettings {
    const defaults = cloneDefaultSettings()
    const stored = readStoredSettings()
    const legacy = readLegacySettings()
    const settings = mergeSettings(mergeSettings(defaults, legacy), stored || {})

    // One-time: settings saved before version 2 still carry the old "on" default.
    const storedVersion = (stored?.version as number | undefined) ?? 1
    if (IS_MACOS_DESKTOP && storedVersion < 2) settings.audio.echoCancellationEnabled = false

    saveAppSettings(settings)
    return settings
}

export function saveAppSettings(settings: AppSettings) {
    if (!canUseLocalStorage()) return

    try {
        localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(normalizeSettings(settings)))
    } catch (e) {
        console.warn('[AppSettings] Failed to save settings to localStorage:', e)
        Sentry.captureException(e, { level: 'warning', extra: { action: 'save_app_settings' } })
    }
}

export function patchAppSettings(patch: DeepPartial<AppSettings>): AppSettings {
    const settings = mergeSettings(loadAppSettings(), patch)
    saveAppSettings(settings)
    return settings
}

/** Reads a participant's persisted relative volume, defaulting to 100% for both mic and screen share. */
export function getParticipantVolume(identity: string): ParticipantVolume {
    return loadAppSettings().participantVolumes[identity] ?? {...DEFAULT_PARTICIPANT_VOLUME}
}

/** Persists a partial volume update (e.g. just `mic` or just `screenShare`) for one participant. */
export function setParticipantVolume(identity: string, patch: Partial<ParticipantVolume>): ParticipantVolume {
    const next = {...getParticipantVolume(identity), ...patch}
    patchAppSettings({participantVolumes: {[identity]: next}})
    return next
}

